import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPendingSpacePlanning, submitSpacePlanningDecisions, fetchFurniturePieces } from '../../api/qcApi';
import { friendlyMessage } from '../../../api/apiClient';
import { LoadingState, ErrorState, InlineAlert } from '../../../shared/components/status';
import {
  boundingBox, fitScale, polygonToLocal, polygonToPathD, placementToLocalRect,
} from '../../../authenticated/components/SpacePlanning/geometry2';

const TILE_SIZE = 220; // px -- fixed box each layout option renders into

// A single layout tile can now represent SEVERAL RoomLayoutOption rows at
// once -- one per combination (within this household) that shares this
// exact room shape (see SpacePlanningQcService's shapeSignature on the
// backend). Joining the id list into one string gives each tile a single,
// stable key to toggle by.
function tileKey(layoutOptionIds) {
  return layoutOptionIds.join(',');
}

// ─── QcLayoutTile ───────────────────────────────────────────────────────────
// Draws one layout option: the room's true outline (footprint + walls,
// using the exact same geometry.js math the customer-facing
// FloorplanCanvas/LayoutThumb already use) with its furniture placements
// composited inside.
//
// The whole tile is the button, same pattern as Material Sourcing QC's
// CandidateCard: clicking it toggles between "hidden" (grey border) and
// "shown to users" (green border + glow). It opens already showing this
// tile's real saved state -- approved, rejected, or never reviewed all
// start from their actual database value, not from a hardcoded default
// -- so a reviewer can see and change a past decision. There's no
// separate toggle button underneath -- that used to eat its own row of
// screen space for something the drawing itself can already fully
// capture as a click target.
//
// Unlike LayoutThumb, there's no "selection" concept here -- a QC
// reviewer isn't picking one layout for a customer to use, they're
// approving or rejecting each layout option independently.
function QcLayoutTile({ layoutOption, displayNumber, furniturePieces, footprintPolygon, wallSegments, approve, onToggle }) {
  const pieceByName = Object.fromEntries((furniturePieces || []).map((p) => [p.name, p]));
  const [minX, maxX, minY, maxY] = boundingBox([footprintPolygon]);
  const widthMm = maxX - minX;
  const heightMm = maxY - minY;
  const scale = fitScale(widthMm, heightMm, TILE_SIZE - 16, TILE_SIZE - 16);
  const viewW = widthMm * scale;
  const viewH = heightMm * scale;

  const localFootprint = polygonToLocal(footprintPolygon, minX, minY, heightMm);
  const footprintPathD = polygonToPathD(localFootprint, scale);
  const isEmpty = layoutOption.unfurnished || !layoutOption.placements?.length;

  return (
    <Box sx={{ width: TILE_SIZE, display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Box
        onClick={onToggle}
        sx={{
          width: TILE_SIZE, height: TILE_SIZE, borderRadius: '6px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
          boxSizing: 'border-box', cursor: 'pointer',
          border: '5px solid',
          borderColor: approve ? '#39FF14' : '#ddd',
          boxShadow: approve ? '0 0 10px 2px rgba(57, 255, 20, 0.65)' : 'none',
          transition: 'border-color 0.1s ease, box-shadow 0.1s ease',
        }}
      >
        <Box component="svg" viewBox={`0 0 ${viewW} ${viewH}`} sx={{ width: viewW, height: viewH }}>
          <path
            d={footprintPathD}
            fill="#fff"
            stroke={isEmpty ? '#000000' : '#FF3131'}
            strokeWidth={isEmpty ? 1.2 : 0.9}
            strokeDasharray={isEmpty ? '4 3' : undefined}
          />

          {!isEmpty && wallSegments.map((segment, i) => {
            const [[x1, y1], [x2, y2]] = polygonToLocal(segment, minX, minY, heightMm);
            return (
              <line key={i} x1={x1 * scale} y1={y1 * scale} x2={x2 * scale} y2={y2 * scale}
                stroke="#000000" strokeWidth={2.5} strokeLinecap="round" />
            );
          })}

          {!isEmpty && layoutOption.placements.map((placement) => {
            const piece = pieceByName[placement.furniturePieceName];
            if (!piece) return null; // unknown stencil name -- skip rather than render a broken image
            const rect = placementToLocalRect(piece, placement, minX, minY, heightMm, scale);
            return (
              <image
                key={placement.id}
                href={piece.imageUrl}
                x={rect.x} y={rect.y} width={rect.width} height={rect.height}
                transform={rect.transform}
                preserveAspectRatio="none"
              />
            );
          })}
        </Box>
      </Box>

      <Typography sx={{ fontSize: '11px', color: '#666', textAlign: 'center' }}>
        {layoutOption.unfurnished ? 'Empty' : `Layout ${displayNumber}`}
      </Typography>
    </Box>
  );
}

// ─── SpacePlanningQc (root) ─────────────────────────────────────────────────
// Reviewed ONE HOUSEHOLD AT A TIME. `phonenumber` comes from the URL (see
// App.js's /qc/space-planning/:phonenumber route and QcHome's phone-number
// field) -- every fetch and the whole review session below is scoped to
// that one household.
export default function SpacePlanningQc() {
  const { phonenumber } = useParams();
  const navigate = useNavigate();

  const [roomGroups, setRoomGroups] = useState(null);
  const [furniturePieces, setFurniturePieces] = useState(null);
  // { [tileKey]: boolean } -- one entry per layout TILE currently on
  // screen (which may represent several database rows), regardless of
  // review state. Seeded from each tile's real `approved` value (see
  // SpacePlanningQcLayoutOptionDto) rather than always starting at false
  // -- an already-approved tile shows up already green, and an
  // already-rejected or never-reviewed tile shows up grey. This is what
  // lets a reviewer revisit and change a past decision instead of every
  // session starting from scratch.
  const [decisions, setDecisions] = useState({});
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setRoomGroups(null);
    setError('');
    Promise.all([fetchPendingSpacePlanning(phonenumber), fetchFurniturePieces()])
      .then(([roomGroupsData, piecesData]) => {
        setRoomGroups(roomGroupsData);
        setFurniturePieces(piecesData);
        const initial = {};
        roomGroupsData.forEach((room) =>
          room.roomUses.forEach((use) =>
            use.pendingLayoutOptions.forEach((opt) => { initial[tileKey(opt.layoutOptionIds)] = !!opt.approved; })
          )
        );
        setDecisions(initial);
      })
      .catch((err) => setError(friendlyMessage(err)));
  };

  // Re-loads whenever the household in the URL changes (e.g. the reviewer
  // navigates from one household's review link straight to another's).
  useEffect(() => { load(); }, [phonenumber]); // eslint-disable-line react-hooks/exhaustive-deps -- reload only when the household changes

  const handleToggle = (key) => {
    setDecisions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      // Expand each tile's key back into one {id, approve} entry per
      // underlying RoomLayoutOption row -- this is what actually applies
      // the same decision to every combination (within this household)
      // that shares this room.
      const payload = Object.entries(decisions).flatMap(([key, approve]) =>
        key.split(',').map((id) => ({ id: Number(id), approve }))
      );
      await submitSpacePlanningDecisions(payload);
      // Refresh -- every tile stays on screen (approved and rejected ones
      // included), now showing its freshly-saved state, so the reviewer
      // can keep adjusting decisions rather than the list shrinking away.
      load();
    } catch (err) {
      // The page stays as it is, so no decisions are lost.
      setSubmitError(friendlyMessage(err, "We couldn't save your decisions. Your choices are still on screen; please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return <ErrorState size="page" title="We couldn't load this review" message={error} onRetry={load} />;
  }
  if (!roomGroups || !furniturePieces) {
    return <LoadingState label="Loading the review…" size="page" />;
  }

  // Every tile currently on screen, decided or not -- submitting always
  // resends one {id, approve} entry per tile, so this drives both the
  // button's enabled state and its count.
  const tileCount = Object.keys(decisions).length;

  return (
    <Box sx={{ maxWidth: 1100, margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      <Box sx={{
        position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        borderBottom: '1px solid #ddd', paddingBottom: '12px', marginBottom: '20px',
      }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', mb: '8px' }}>
          <Box
            component="button"
            onClick={() => navigate('/qc')}
            sx={{
              justifySelf: 'start',
              fontSize: '13px', fontWeight: 600, padding: '10px 18px', borderRadius: '3px',
              cursor: 'pointer', background: '#475569', color: '#fff', border: 'none',
            }}
          >
            ← QC Home
          </Box>
          <Typography sx={{ justifySelf: 'center', fontSize: '20px', fontWeight: 600 }}>
            Space Planning QC
          </Typography>
          <Box
            component="button"
            onClick={handleSubmit}
            disabled={submitting || tileCount === 0}
            sx={{
              justifySelf: 'end',
              fontSize: '13px', fontWeight: 600, padding: '10px 18px', borderRadius: '3px',
              cursor: tileCount === 0 ? 'default' : 'pointer',
              background: tileCount === 0 ? '#ccc' : '#16a34a', color: '#fff', border: 'none',
            }}
          >
            {submitting ? 'Submitting…' : `Submit (${tileCount})`}
          </Box>
        </Box>

        {submitError && <InlineAlert severity="error" sx={{ mb: 1 }}>{submitError}</InlineAlert>}
        <Typography sx={{ fontSize: '12px', color: '#666' }}>
          Reviewing household <b>{phonenumber}</b>. A green border means <b>shown to users</b>; grey
          means hidden. This includes layouts you've already decided on, not just new ones — click any
          tile to flip it, then Submit to save your changes.
        </Typography>
      </Box>

      {roomGroups.length === 0 ? (
        <Typography sx={{ color: '#666' }}>
          Nothing generated for {phonenumber} yet — double check the phone number, or check
          back once the layout pipeline has run for this household.
        </Typography>
      ) : (
        // One card per physical ROOM, not per combination -- a room that's
        // shared across several of this household's combinations only
        // needs reviewing once.
        roomGroups.map((room, roomIndex) => (
          <Box key={roomIndex} sx={{ mb: '32px', border: '1px solid #eee', borderRadius: '6px', padding: '16px' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>{room.appliesTo[0]?.roomLabel}</Typography>
            <Typography sx={{ fontSize: '12px', color: '#666', mb: '16px' }}>
              Applies to: {room.appliesTo.map((a) => `${a.combinationName} (${a.roomLabel})`).join(', ')}
            </Typography>

            {room.roomUses.map((use) => {
              let layoutCount = 0; // 1-based position among FURNISHED tiles only, per room-use
              return (
                <Box key={use.name} sx={{ mb: '14px' }}>
                  <Typography sx={{ fontSize: '13px', color: '#444', mb: '8px' }}>
                    Room Use: {use.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                    {use.pendingLayoutOptions.map((opt) => {
                      const key = tileKey(opt.layoutOptionIds);
                      const displayNumber = opt.unfurnished ? null : ++layoutCount;
                      return (
                        <QcLayoutTile
                          key={key}
                          layoutOption={opt}
                          displayNumber={displayNumber}
                          furniturePieces={furniturePieces}
                          footprintPolygon={room.footprintPolygon}
                          wallSegments={room.wallSegments}
                          approve={!!decisions[key]}
                          onToggle={() => handleToggle(key)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))
      )}
    </Box>
  );
}